import { EditorKeyBindingCommand } from 'const';

// The current file repeats the draftjs functionality
// getDefaultKeyBinding is used in Image cell so we decided to move it here to get rid of draftjs dependencies

// Modernised version of Draft‑JS `getDefaultKeyBinding`
// ‑ Replaces deprecated `KeyboardEvent.keyCode` with `KeyboardEvent.key`
// ‑ Written in TypeScript for better DX
// ‑ Keeps the same output contract: returns a `DraftEditorCommand` string or `null`
//   so it is a drop‑in replacement for `keyBindingFn`.

/**
 * A subset of DraftEditorCommand strings referenced by the original helper.
 * Extend this union with anything your codebase needs.
 */
type DraftEditorCommand =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'code'
  | 'undo'
  | 'redo'
  | 'delete'
  | 'delete-word'
  | 'backspace'
  | 'backspace-word'
  | 'backspace-to-start-of-line'
  | 'split-block'
  | 'transpose-characters'
  | 'secondary-cut'
  | 'secondary-paste'
  | 'move-selection-to-start-of-block'
  | 'move-selection-to-end-of-block';

/** Cross‑platform helpers (re‑implemented so we don’t depend on Draft internals) */
const isMacOS = /Mac|iPad|iPhone|iPod/.test(navigator.platform);

/**
 * DraftJS's original util treated "Ctrl" the same on both platforms, as long as
 * Alt/Option is not pressed (to avoid the AltGraph combo).
 */
const isCtrlKeyCommand = (e: KeyboardEvent): boolean => !!e.ctrlKey && !e.altKey;

/**
 * `hasCommandModifier` mirrors ⌘ on macOS and Ctrl on other OSes, while also
 * explicitly excluding the Alt/Option key so that Ctrl+Alt (AltGraph) doesn't
 * count as a command‑modifier.
 */
const hasCommandModifier = (e: KeyboardEvent): boolean =>
  isMacOS ? !!e.metaKey && !e.altKey : isCtrlKeyCommand(e);

const shouldRemoveWord = (e: KeyboardEvent): boolean =>
  (isMacOS && e.altKey) || isCtrlKeyCommand(e);

const getBackspaceCommand = (e: KeyboardEvent): DraftEditorCommand => {
  if (hasCommandModifier(e) && isMacOS) {return 'backspace-to-start-of-line';}

  return shouldRemoveWord(e) ? 'backspace-word' : 'backspace';
};

const getDeleteCommand = (e: KeyboardEvent): DraftEditorCommand | null => {
  // Allow default "cut" behaviour for Windows on Shift + Delete
  if (!isMacOS && e.shiftKey) {return null;}

  return shouldRemoveWord(e) ? 'delete-word' : 'delete';
};

/**
 * Map letter keys (case‑insensitive) to the command they trigger when combined
 * with the appropriate modifiers. Using a map keeps the bottom switch concise.
 */
const alphaCommands: Record<string, (e: KeyboardEvent) => DraftEditorCommand | null> = {
  b: e => (hasCommandModifier(e) ? 'bold' : null),
  d: e => (isCtrlKeyCommand(e) ? 'delete' : null),
  h: e => (isCtrlKeyCommand(e) ? 'backspace' : null),
  i: e => (hasCommandModifier(e) ? 'italic' : null),
  j: e => (hasCommandModifier(e) ? 'code' : null),
  k: e => (isMacOS && isCtrlKeyCommand(e) ? 'secondary-cut' : null),
  m: e => (isCtrlKeyCommand(e) ? 'split-block' : null),
  o: e => (isCtrlKeyCommand(e) ? 'split-block' : null),
  t: e => (isMacOS && isCtrlKeyCommand(e) ? 'transpose-characters' : null),
  u: e => (hasCommandModifier(e) ? 'underline' : null),
  w: e => (isMacOS && isCtrlKeyCommand(e) ? 'backspace-word' : null),
  x: e => (hasCommandModifier(e) && e.shiftKey ? 'strikethrough' : null),
  y: (e) => {
    if (!isCtrlKeyCommand(e)) {return null;}

    return isMacOS ? 'secondary-paste' : 'redo';
  },
  z: (e) => {
    if (!hasCommandModifier(e)) {return null;}

    return e.shiftKey ? 'redo' : 'undo';
  },
};

/**
 * Firefox < 29 on macOS had a bug causing ⌘+←/→ to navigate away instead of
 * moving the caret within the editor. The original fix relied on UA sniffing.
 * Modern browsers no longer suffer from this, so we skip that quirk unless you
 * explicitly re‑enable it.
 */
const SHOULD_FIX_FF_MOVEMENT = false;

export function getDefaultKeyBinding(e: KeyboardEvent): DraftEditorCommand | null {
  // First handle printable A‑Z keys via the map
  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key; // letter or named key

  if (key in alphaCommands) {
    return alphaCommands[key](e);
  }

  // Non‑printable / navigation keys
  switch (key) {
    case 'Enter':
      return 'split-block';

    case 'Delete':
      return getDeleteCommand(e);

    case 'Backspace':
      return getBackspaceCommand(e);

    case 'ArrowLeft':
      return SHOULD_FIX_FF_MOVEMENT && hasCommandModifier(e)
        ? 'move-selection-to-start-of-block'
        : null;

    case 'ArrowRight':
      return SHOULD_FIX_FF_MOVEMENT && hasCommandModifier(e)
        ? 'move-selection-to-end-of-block'
        : null;

    default:
      return null;
  }
}

export const undoRedoKeyBindings = (e: KeyboardEvent): string | null => {
  if (e.ctrlKey || e.metaKey) {
    if (e.code === 'KeyZ' && !e.shiftKey) { // ctrl/cmd + z
      e.preventDefault();

      return EditorKeyBindingCommand.UNDO;
    } else if ((e.code === 'KeyZ' && e.shiftKey) || e.code === 'KeyY') { // ctrl/cmd + shift + z and ctrl/cmd + y
      e.preventDefault();

      return EditorKeyBindingCommand.REDO;
    }
  }

  return getDefaultKeyBinding(e);
};
